/* ============================================================================
   Curated Collection JS  —  v2026-09-25 "no-blink"
   Pins one product to slot #1 of a collection grid, in the variant (color)
   from the link.

   Links:  /collections/<handle>?first=<product-handle | product URL | variant ID>&variant=<id>
           (a bare variant ID works with Meta's {{product.retailer_id}} tag)

   How it works:
   - Keeps the grid Shopify already rendered (no wipe, no "Loading…").
   - Fetches ONLY the pinned product's card and puts it first.
   - Removes that product's duplicate lower in the grid. If it wasn't on the
     page, the grid simply shows one extra card (no product is ever dropped).
   - Swaps the pinned card to the variant's photo and links it to that variant.
   - Does nothing at all unless the link contains ?first=
   - `limit` and `source` from the old version are ignored (links still work).
============================================================================ */

(() => {
  const qp = new URLSearchParams(location.search);
  const rawFirst = (qp.get('first') || '').trim();
  if (!rawFirst) return;

  /* ---------- 1. Work out the product handle + variant ---------- */
  let firstHandle = '';
  let variantFromURL = '';
  let variantOnly = '';
  let decoded = rawFirst;
  try { decoded = decodeURIComponent(rawFirst); } catch (_) {}

  if (/^https?:/i.test(decoded)) {
    try {
      const u = new URL(decoded);
      const segs = u.pathname.split('/').filter(Boolean);
      const i = segs.indexOf('products');
      if (i > -1 && segs[i + 1]) firstHandle = segs[i + 1];
      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) {}
  } else if (/^\d+$/.test(decoded)) {
    variantOnly = decoded; // Meta {{product.retailer_id}} = Shopify variant ID
  } else {
    firstHandle = decoded.split('?')[0].split('/').filter(Boolean).pop() || '';
  }
  if (!firstHandle && !variantOnly) return;

  const vRaw = qp.get('variant') || variantFromURL || variantOnly;
  const variantID = /^\d+$/.test(vRaw) ? vRaw : '';

  const grid = document.querySelector('ul#product-grid');
  if (!grid) return;

  /* ---------- 2. Theme preview support (for testing unpublished themes) ---------- */
  const previewExtras = ['preview_theme_id', 'preview_theme_token', 'pb']
    .filter(k => qp.has(k))
    .map(k => `${k}=${encodeURIComponent(qp.get(k) || '')}`)
    .join('&');
  const withPreview = url =>
    previewExtras ? url + (url.includes('?') ? '&' : '?') + previewExtras : url;

  /* ---------- 3. Fetch pinned card + variant data in parallel ---------- */
  const aspect = (grid.getAttribute('data-aspect') || '').toLowerCase();
  const view = aspect === 'square' ? 'card-square'
             : aspect === 'portrait' ? 'card-portrait'
             : 'card';

  // Resolve the product handle (from a bare variant ID if needed)
  const handlePromise = firstHandle
    ? Promise.resolve(firstHandle)
    : fetch(`/variants/${variantOnly}`)
        .then(r => {
          if (!r.ok) return '';
          const segs = new URL(r.url).pathname.split('/').filter(Boolean);
          const i = segs.indexOf('products');
          return i > -1 ? (segs[i + 1] || '') : '';
        })
        .catch(() => '');

  const cardPromise = handlePromise.then(async h => {
    if (!h) return '';
    firstHandle = h;
    const cacheKey = `pin:${view}:${h}:${variantID || 'x'}`;
    const cardURL = withPreview(
      `/products/${encodeURIComponent(h)}?view=${view}` +
      (variantID ? `&variant=${variantID}` : '')
    );
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return cached;
    } catch (_) {}
    const r = await fetch(cardURL);
    if (!r.ok) return '';
    const html = (await r.text()).trim();
    try { if (html) sessionStorage.setItem(cacheKey, html); } catch (_) {}
    return html;
  }).catch(() => '');

  const variantPromise = variantID
    ? handlePromise
        .then(h => (h ? fetch(`/products/${encodeURIComponent(h)}.js`) : null))
        .then(r => (r && r.ok ? r.json() : null))
        .then(p => (p && p.variants || []).find(v => String(v.id) === variantID) || null)
        .catch(() => null)
    : Promise.resolve(null);

  /* ---------- 4. Helpers ---------- */
  const handleOf = li => {
    const a = li.querySelector('a[href*="/products/"]');
    if (!a) return '';
    try {
      const segs = new URL(a.getAttribute('href'), location.origin).pathname.split('/').filter(Boolean);
      const i = segs.indexOf('products');
      return i > -1 ? (segs[i + 1] || '') : '';
    } catch (_) { return ''; }
  };

  const sizedSrc = (src, w) => {
    const base = src.startsWith('//') ? 'https:' + src : src;
    return base + (base.includes('?') ? '&' : '?') + 'width=' + w;
  };

  const applyVariant = (li, variant) => {
    if (!variantID) return;
    li.querySelectorAll('a[href*="/products/"]').forEach(a => {
      try {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('variant', variantID);
        a.setAttribute('href', u.pathname + u.search);
      } catch (_) {}
    });
    const img = variant && variant.featured_image && variant.featured_image.src;
    if (!img) return;
    const imgs = li.querySelectorAll('.card__media img, .media img, img');
    const main = imgs[0];
    if (!main) return;
    main.loading = 'eager';
    main.srcset = [360, 533, 720, 940, 1066].map(w => `${sizedSrc(img, w)} ${w}w`).join(', ');
    main.src = sizedSrc(img, 720);
  };

  /* ---------- 5. Insert pinned card ---------- */
  (async () => {
    const [html, variant] = await Promise.all([cardPromise, variantPromise]);
    if (!html) return; // product missing/drafted → leave the normal grid alone

    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const pinned = tpl.content.querySelector('li') || null;
    if (!pinned) return;
    pinned.classList.add('grid__item', 'bt-pinned');
    pinned.classList.remove('scroll-trigger', 'animate--slide-in');

    applyVariant(pinned, variant);

    // Only remove the pinned product's own duplicate. Never drop another
    // product (it wouldn't appear on page 2 either).
    const items = [...grid.querySelectorAll(':scope > li')];
    const dupe = items.find(li => handleOf(li) === firstHandle);
    if (dupe) dupe.remove();

    grid.prepend(pinned);
  })().catch(() => { /* on any error the normal grid stays as-is */ });
})();
